// @ts-check
import _isString from 'lodash/isString';
import _isNumber from 'lodash/isNumber';
import {
  addDays,
  differenceInMilliseconds,
  differenceInYears,
  eachMinuteOfInterval,
  format,
  formatDistance,
  formatDuration,
  intervalToDuration,
  isValid,
  parse,
  startOfYesterday,
  subYears,
  isToday,
  isYesterday,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { PERFORMANCE_DEFAULT_DATES, BG_TIMEZONE } from 'constants/index';

/**
 * Format date for note
 * @param {string} date // '2019-08-04T04:08:30+02:00'
 */
const getNoteDate = (date) => format(new Date(date), 'MMM dd, yyyy');

/**
 * Get formatted future date based on provided date param
 * Example: if today is 12/8/2021 and provided date is 10, then it will return 12/18/2021
 * getFutureDateFromToday(10) => '12/18/2021
 * @param {number} days
 */
const getFutureDateFromToday = (days) => {
  if (!_isNumber(days)) return '';

  const futureDate = addDays(new Date(), days);
  return format(futureDate, 'MM/dd/yyyy');
};

/**
 * Format date to US style - MM/DD/YYYY
 * Prevents adjustment of date based on browser timezone
 * @param {string} date // '2021-03-09T00:00:00.000Z' or '20210309 '
 */
const getUSFormatDate = (date = '') => {
  try {
    return format(new Date(date.substring(0, 10).replace(/-/g, '/')), 'MM/dd/yyyy');
  } catch (e) {
    const formattedDate = parse(date.trim(), 'yyyyddmm', new Date());
    return format(formattedDate, 'MM/dd/yyyy');
  }
};

/**
 * Format date to YYYY-MM-DD
 * @param {string} date // '2021-03-09T00:00:00.000Z'
 */
const getDateFormatYYYYMMDD = (date) => format(new Date(date), 'yyyy-MM-dd');

/**
 * Return same date in prior year
 * Provide with number of years to go back
 * Optional: Provide with date object or function defaults to todays date
 * Optional: Provide with negative number to return a future year
 * @param {number} years // 3
 * @param {string} [date] // '2021-03-09T00:00:00.000Z'
 */
const getPriorYear = (years, date) => {
  const dateObj = date ? new Date(date) : new Date();
  return subYears(dateObj, years);
};

/**
 * Sort array by date
 * @param {array} arr
 */
const sortByDate = (arr) =>
  arr.sort((a, b) => differenceInMilliseconds(new Date(b.lastUpdatedTime), new Date(a.lastUpdatedTime)));

/**
 * Format date for note details
 * @param {string} date
 */
const formatNoteDetailsDate = (date) => format(new Date(date), 'MMM dd, yyyy');

/**
 * Format date for note details + time
 * @param {string} date
 */
const formatNoteDetailsDateTime = (date) => format(new Date(date), "MMM dd, yyyy 'at' h:mm a");

/**
 * checks if timestamp is valid
 * @param {any} timestamp
 * @returns {boolean}
 */
const isValidTimeStamp = (timestamp) => typeof timestamp === 'number' && !!new Date(timestamp).getTime();

/**
 * Check validity of date object
 * Returns provided date if date is formatted correctly for a valid date obj
 * Return false if provided date is not correct format for a valid date obj
 */
const checkDateValidity = (date = new Date()) => isValid(new Date(date));

/**
 * Creates timestamp from date-string in given format, used to validate date Objects
 * Returns new date object
 * Returns current date if !date or if date timestamp is invalid
 * @param {Date | String} date
 * @param {string} [pattern] 'yyyy-MM-dd'
 */
const getTimestampFromDateString = (date, pattern = 'yyyy-MM-dd') => {
  if (!date) return Date.now();

  let formattedDate;

  try {
    formattedDate = format(new Date(date), pattern);
  } catch (err) {
    try {
      // @ts-ignore
      formattedDate = new Date(date.replace(/-/g, '/'));
    } catch {
      // RangeError: Invalid time value
      formattedDate = '';
    }
  }

  const timestamp = new Date(formattedDate).getTime();
  const returnableDate = date instanceof Date ? date : new Date(getUSFormatDate(date));

  return isValidTimeStamp(timestamp) ? returnableDate : Date.now();
};

/**
 * Format date to MM/dd/yyyy
 * @param {Date | String} date
 */
const formatDateMMDDYYYY = (date) => format(getTimestampFromDateString(date), 'MM/dd/yyyy');

/**
 * Format date to MMddyyyy
 * @param {Date | String} date
 */
const formatDateWithoutSlashMMDDYYYY = (date) => format(getTimestampFromDateString(date), 'MMddyyyy');

/**
 * Format date to MMddyyyy
 * @param {Date | String} date
 */
const formatTimeHHMMSS = (date) => format(getTimestampFromDateString(date), 'HHMMSS');

/**
 * Format date to yyyy-MM-dd
 * @param {Date | String} date
 */
const formatDateYYYYMMDD = (date) => format(getTimestampFromDateString(date), 'yyyy-MM-dd');

/**
 * Format date to MM.dd.yyyy
 * @param {Date | String} date
 */
const formatDateWithDotMMDDYYYY = (date) => format(getTimestampFromDateString(date), 'MM.dd.yyyy');

/**
 * converts timestamps to relative-date string
 * @param {any} stamp timestamp
 * @returns {string} relative date
 */
const timeStampToRelativeDate = (stamp) => formatDistance(new Date(stamp), Date.now(), { addSuffix: true });

/**
 * @param {String} created
 * @param {String} updated
 * @param {Boolean} withTitle
 * @returns {string}
 */
const getWishlistDisplayedDate = (created, updated, withTitle = true) => {
  const createdTimestamp = new Date(created).getTime();
  const updatedTimestamp = new Date(updated).getTime();
  // If difference in updated and created timestamp ar more than this number then wishlist was updated
  const minimumDifference = 1000;

  if (updatedTimestamp - createdTimestamp > minimumDifference) {
    return `${withTitle ? 'Last updated on: ' : ''}${formatNoteDetailsDate(updated)}`;
  }

  return `${withTitle ? 'Created on: ' : ''}${formatNoteDetailsDate(created)}`;
};

/**
 * Sort array by timestamp value
 * @param {array} array
 * @param {function} [cb] - optional callback to perform on array
 * @returns {array} sorted array
 */
const sortByTimestamp = (array, cb) => {
  const newArray = cb ? cb(array) : [...array];
  return newArray.sort((a, b) => differenceInMilliseconds(new Date(b.timestamp), new Date(a.timestamp)));
};

const dateToISOString = (date) => new Date(parseInt(date, 10)).toISOString();

const getMonth = (date) => {
  try {
    return format(new Date(date), 'M');
  } catch {
    // For compatibility with previous moment behavior
    return 'Invalid date';
  }
};

const convertKebabCaseDateString = (dateString) => {
  const dateStringRegExp = /^\d{4}-\d{2}-\d{2}/;

  // check if the incoming value is string of the next format 'YYYY-MM-DD'
  if (_isString(dateString) && dateStringRegExp.test(dateString)) {
    const dateStringNoTimestamp = dateString.substring(0, 10);
    // convert date string from 'YYYY-MM-DD' format to 'YYYY/MM/DD' to avoid issue with local date
    return dateStringNoTimestamp.replace(/-/g, '/');
  }

  return dateString;
};

const isDateOfMMDDYYYFormat = (dateString) => {
  const MM_DD_YYYY_REGEX = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/;
  return dateString.match(MM_DD_YYYY_REGEX);
};

const yearDiffFromToday = (dateString) => {
  const dateEntered = new Date(dateString);
  return differenceInYears(startOfYesterday(), dateEntered);
};

/**
 * Format time difference of start time and end time
 * @param {string} start // '2021-06-11T22:18:12.000Z'
 * @param {string} end // '2021-06-11T23:38:42.000Z'
 * @returns {string} // '1h 20m 30s'
 */
const computeLapsedTime = (start, end) => {
  const formatDistanceLocale = { xSeconds: '{{count}}s', xMinutes: '{{count}}m', xHours: '{{count}}h' };
  const shortEnLocale = { formatDistance: (token, count) => formatDistanceLocale[token].replace('{{count}}', count) };

  const duration = intervalToDuration({ start: new Date(start), end: new Date(end) });
  return formatDuration(duration, { format: ['hours', 'minutes', 'seconds'], locale: shortEnLocale }) || '0s';
};

/**
 * Get valid date from MM-DD-YYYY format dates
 * @param {string} dateString // '2021-06-11T22:18:12.000Z' or 'MM-DD-YYYY' or 'MM/DD/YYYY'
 * @returns {Date} // valid date like '2021-06-11T22:18:12.000Z'
 */
const getValidDateFromMMDDYYYY = (dateString) => {
  const dateDelimiter = /[-/]/;
  let validDate = new Date(dateString);

  if (dateString.length === 10 && dateDelimiter.test(dateString)) {
    const [month, day, year] = dateString.split(dateDelimiter);
    validDate = new Date(+year, +month - 1, +day);
  }
  return validDate;
};

//const returnableDate = date instanceof Date ? date : new Date(getUSFormatDate(date));

/**
 * Format time difference of start time and today time
 * @param {string} start // '2021-06-11T22:18:12.000Z' or 'MM-DD-YYYY' or 'MM/DD/YYYY'
 * @param {string | undefined | Date } today // '2021-06-11T22:18:12.000Z' or 'MM-DD-YYYY' or 'MM/DD/YYYY'
 * @returns {string} // 'Added today' 'Added yesterday', 'Added 4 days ago', 'Added 2 months ago', etc
 */
const computeTimeAgo = (start, today = new Date()) => {
  const startDate = getValidDateFromMMDDYYYY(start);
  const endDate = today instanceof Date ? today : getValidDateFromMMDDYYYY(today);

  const duration = intervalToDuration({ start: startDate, end: endDate });
  const getPeriod = () => {
    const { years, months, days } = duration || {};
    if (years && years > 0) {
      if (years === 1) {
        Object.assign(duration, { year: 1 });
        return 'year';
      }
      return 'years';
    }
    if (months && months > 0) {
      if (months === 1) {
        Object.assign(duration, { month: 1 });
        return 'month';
      }
      return 'months';
    }
    if (days && days > 1) return 'days';
    return '';
  };

  const formatTimeAgo = (startTime) => {
    const selectedPeriod = getPeriod();
    if (isToday(startTime) || duration?.days === 0) return 'Added Today';
    if (isYesterday(startTime) || duration?.days === 1) return 'Added Yesterday';
    const durationCount = duration[selectedPeriod] === 1 ? 'a' : duration[selectedPeriod];
    return `Added ${durationCount} ${selectedPeriod} ago`;
  };
  return formatTimeAgo(new Date(start));
};

const getDateParams = ({ filter, startDate, endDate, keyNames = { from: 'fromDate', to: 'toDate' } }) => {
  if (filter === PERFORMANCE_DEFAULT_DATES.customRange) {
    return {
      [keyNames.from]: formatDateYYYYMMDD(startDate),
      [keyNames.to]: formatDateYYYYMMDD(endDate),
    };
  }

  return {};
};

/**
 * Returns the list of minutes interval between two hours formatted with 'HH::mm'
 * @param {number} startHour
 * @param {number} endHour
 */
const getEachMinuteOfInterval = (startHour = 0, endHour = 0) =>
  eachMinuteOfInterval({
    start: new Date().setHours(startHour, 0),
    end: new Date().setHours(endHour, 0),
  })
    .map((d) => format(d, 'HH:mm'))
    .slice(0, -1);

// date - is an ISO format yyyy-MM-dd
const _isToday = (date = '', timeZone = BG_TIMEZONE) =>
  !!date && date === formatInTimeZone(Date.now(), timeZone, 'yyyy-MM-dd');

const getCurrentLocalTimeHHMM = (timeZone = BG_TIMEZONE) => formatInTimeZone(Date.now(), timeZone, 'HH:mm');
const getCurrentWeekDayISO = (timeZone = BG_TIMEZONE) => formatInTimeZone(new Date(), timeZone, 'i');

export {
  getNoteDate,
  sortByDate,
  formatNoteDetailsDate,
  formatNoteDetailsDateTime,
  getWishlistDisplayedDate,
  checkDateValidity,
  formatDateMMDDYYYY,
  isValidTimeStamp,
  formatDateYYYYMMDD,
  timeStampToRelativeDate,
  sortByTimestamp,
  dateToISOString,
  getMonth,
  getUSFormatDate,
  getDateFormatYYYYMMDD,
  getPriorYear,
  convertKebabCaseDateString,
  isDateOfMMDDYYYFormat,
  computeLapsedTime,
  yearDiffFromToday,
  getFutureDateFromToday,
  getDateParams,
  getEachMinuteOfInterval,
  formatDateWithDotMMDDYYYY,
  computeTimeAgo,
  getValidDateFromMMDDYYYY,
  formatDateWithoutSlashMMDDYYYY,
  formatTimeHHMMSS,
  getCurrentWeekDayISO,
  getCurrentLocalTimeHHMM,
  _isToday,
};
